import { useState } from "react"
import { Chevron } from "@/components/Accordion/Accordion"
import CardPeek from "@/components/CardFace/CardPeek"
import CardThumb from "@/components/CardFace/CardThumb"
import EmptyState from "@/components/EmptyState/EmptyState"
import Pill from "@/components/Pill/Pill"
import { CARDS_COPY } from "@/content/cardsRound"
import { useSessionStore } from "@/store/sessionStore"
import { picksBy, selectCurrentDoneKey, selectPicks, selectSession } from "@/store/selectors"
import styles from "@/styles/pages/Cards.module.css"

/*
 * Who has picked what, for the facilitator only. A picker seeing the others'
 * hands mid-round would anchor them, which is the same failure the Pains round
 * hides its notes to avoid.
 *
 * It rides in the rail under `FacilitatorPanel`, not inside the round — the
 * paper panel is the table the cards are dealt on, and a roster of other
 * people's hands is not one of the cards. Which is also why it is mounted by
 * `Session` rather than by `CardsPick`: the rail is the shell's, and a panel
 * cannot render itself into a sibling column.
 */

export default function PickMonitor() {
  const session = useSessionStore(selectSession)
  const picks = useSessionStore(selectPicks)
  const participants = useSessionStore((s) => s.participants)
  const doneKey = useSessionStore(selectCurrentDoneKey)
  const [open, setOpen] = useState(true)

  if (!session) return null

  const cap = session.config.handCap ?? 0

  return (
    <section className={styles.monitor}>
      <div className={styles.monitorHead}>
        <h2 className={styles.monitorTitle}>{CARDS_COPY.monitorTitle}</h2>
        <button
          type="button"
          className={styles.monitorCollapse}
          title={open ? CARDS_COPY.hideMonitor : CARDS_COPY.showMonitor}
          aria-label={open ? CARDS_COPY.hideMonitor : CARDS_COPY.showMonitor}
          aria-expanded={open}
          onClick={() => setOpen((shown) => !shown)}
        >
          <Chevron open={open} />
        </button>
      </div>

      {open ? (
        participants.length === 0 ? (
          <EmptyState title={CARDS_COPY.emptyShortlist} />
        ) : (
          <ul className={styles.monitorRows}>
            {participants.map((participant) => {
              const theirs = picksBy(picks, participant.id)
              const finished = participant.donePhase === doneKey
              const empties = cap > 0 ? Math.max(cap - theirs.length, 0) : 0
              return (
                <li key={participant.id} className={styles.monitorRow}>
                  <span className={styles.monitorRowHead}>
                    <span className={styles.monitorName}>{participant.name}</span>
                    <Pill tone={finished ? "done" : "warning"} dot pulse={!finished}>
                      {finished ? CARDS_COPY.stateDone : CARDS_COPY.statePicking}
                    </Pill>
                  </span>
                  {cap === 0 && theirs.length === 0 ? (
                    <span className={styles.monitorEmpty}>{CARDS_COPY.pickedNothing}</span>
                  ) : (
                    <span className={styles.monitorCards}>
                      {theirs.map((cardId) => (
                        <CardPeek key={cardId} cardId={cardId}>
                          <CardThumb cardId={cardId} size="xs" />
                        </CardPeek>
                      ))}
                      {Array.from({ length: empties }, (_, index) => (
                        <span
                          key={`empty-${index}`}
                          className={styles.monitorSlot}
                          aria-hidden="true"
                        >
                          {theirs.length + index + 1}
                        </span>
                      ))}
                      <span className={styles.monitorCount}>
                        {CARDS_COPY.dealPicked(theirs.length, cap)}
                      </span>
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )
      ) : null}
    </section>
  )
}
